"use client";
import { ButtonComponent, LoaderComponent, SnackbarComponent } from "@/components";
import { useRouter } from "@/i18n/routing";
import {
  GET_PRODUCT_APPLICATION,
  getKeyByValue,
  IMAGES,
  KYCSTATUS,
  MOENGAGE_EVENTS,
  PRODUCT_HANDLER,
  PRODUCT_TYPES,
  PRODUCTS,
  STAGE,
  STAGE_BASED_ROUTING,
  SUB_STAGE_FAILS,
  SUBSTAGE,
  WEB_COLORS,
} from "@/lib/constants";
import useErrorHandling from "@/lib/hooks/errorhandling";
import checkPopup from "@/lib/hooks/popupChecker";
import useStageErrorHandler from "@/lib/hooks/stageErrorHandler";
import {
  useGetApplicationQuery,
  useGetCustomerQuery,
  useGetCustomerStatusQuery,
  useGetProductsListQuery,
} from "@/lib/redux";
import { setModalAttributes } from "@/lib/redux/slice/ModalSlice";
import FaceVerificationModal from "@/screens/product-screens/kyc/face-verification/face-verification-modal";
import ErrorModal from "@/screens/product-screens/mandate/repayment-mode/error-modal";
import moengage from "@moengage/web-sdk";
import { Grid, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import ProductCard from "./product-card";
import CardBanner from "@/components/CardBanner";
import ENV from "@/lib/constants/EnvVariables";
import PreApprovedOffers from "../pre-approved-offers";
import { ERROR_TYPES } from "@/lib/utils/errorhandling.utils";
import { EKYC_MESSAGES } from "@/lib/utils/ekyc.utils";
import { sessionStorageUtil } from "@/lib/utils/common.utils";

const ProductsList = ({ tokenExist }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const t = useTranslations("Dashboard");
  const m = useTranslations("ErrorModal.mandateLimitExhausted");
  const { data: productList, isLoading } = useGetProductsListQuery();
  // fetch application data from get api
  const {
    data: applicationData,
    error: errorGetApplication,
    isLoading: isLoadingGetApplication,
    isFetching: isFetchingGetApplication,
    refetch: refetchApplicationData,
  } = useGetApplicationQuery(undefined, {
    skip: !tokenExist,
  });
  const { data: customerData } = useGetCustomerQuery("", { skip: !tokenExist });
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCTS?.PL,
    applicationData?.data,
  );
  // const {
  //   data: customerStatus,
  //   isLoading: customerStatusLoading,
  //   isError: customerStatusError,
  // } = useGetCustomerStatusQuery(undefined, {
  //   skip: !tokenExist,
  // });

  const isError = errorGetApplication;

  /***************************************EFFECT************************************ */
  useErrorHandling(isError);
  useStageErrorHandler({
    stage: productApplication?.stage,
    prevStage: productApplication?.previousStage,
    message: productApplication?.loanReapplyMessage,
  });

  const handleCheckCredit = (e) => {
    if (sessionStorage?.token) {
      if (
        !customerStatus?.data?.panProfileReady ||
        !customerStatus?.data?.hasScore
      ) {
        router.push(`/credit-score/pan`);
      } else if (
        customerStatus?.data?.panProfileReady &&
        customerStatus?.data?.hasScore
      ) {
        router.push("/credit-score/details");
      }
    } else {
      router.push(`/credit-score`);
    }
  };

  const handleCheckEligibilty = (e) => {
    sessionStorage.setItem("check_eligibility", true);
    if (!tokenExist) {
      router.push(`/personal-loan`);
    } else {
      router.replace(`/check-eligibility`);
    }
  };
  
  const handleProuctCardClick = useCallback(async (product) => {
    if (product?.comingSoon) return;
    if (product?.url) {
      checkPopup(product.url);
      return;
    }
    const token = sessionStorageUtil.get("token");

    if (!token) {
      moengage.trackEvent(MOENGAGE_EVENTS?.HOME_CARD_CLICKED, {
        phone: customerData?.data?.phone || "",
        product_type: product?.loanType || "",
      });
      router.push(`/${getKeyByValue(PRODUCT_HANDLER, product?.loanType)}`);
      return;
    }
    let applicationData;
    try {
      applicationData = await refetchApplicationData()?.unwrap();
    } catch (error) {
      console.error("Failed to fetch application data:", error);
      return;
    }
    const productApplication = GET_PRODUCT_APPLICATION(
      PRODUCTS?.PL,
      applicationData?.data,
    );

    const hasAllMandateCountExhausted =
      productApplication?.eMandateRetryCount ===
        ENV.MAX_E_MANDATE_RETRY_COUNT &&
      productApplication?.upiMandateRetryCount ===
        ENV.MAX_UPI_MANDATE_RETRY_COUNT;

    moengage.trackEvent(MOENGAGE_EVENTS?.HOME_CARD_CLICKED, {
      phone: customerData?.data?.phone || "",
      appId: productApplication?.appId || "",
      product_type: product?.loanType || "",
    });
    const stage = productApplication?.stage || applicationData?.data?.stage;
    if (stage === "CLOSED") {
      dispatch(
        setModalAttributes({
          open: true,
          message: productApplication?.loanReapplyMessage,
          type: productApplication?.stage,
        }),
      );
    } else if (
      stage === STAGE?.DIGI_KYC &&
      productApplication?.subStage ===
        KYCSTATUS?.PAN_KYC_NAME_MATCH_CREDIT_QUEUE
    ) {
      dispatch(
        setModalAttributes({
          open: true,
          message:
            productApplication?.message ||
            "Looks like, we would require to screen your application a little more. We will get back to you in next 24 hours",
          type: KYCSTATUS?.PAN_KYC_NAME_MATCH_CREDIT_QUEUE,
        }),
      );
    } else if (
      stage === STAGE?.DIGI_KYC &&
      productApplication?.subStage === SUBSTAGE?.DIGI_KYC_FAIL_FACEMATCH
    ) {
      dispatch(
        setModalAttributes({
          open: true,
          message:
            productApplication?.message ||
            "Looks like, we would require to screen your application a little more. We will get back to you in next 24 hours",
          type: SUBSTAGE?.DIGI_KYC_FAIL_FACEMATCH,
        }),
      );
    } else if (
      productApplication?.stage === STAGE.CREDIT_QUEUE &&
      productApplication?.subStage ===
        SUBSTAGE?.KYC_BANK_NAME_MATCH_CREDIT_QUEUE
    ) {
      dispatch(
        setModalAttributes({
          open: true,
          message:
            productApplication?.message ||
            `The bank account must belong to the person applying for the loan ${customerData?.data?.firstName} ${customerData?.data?.lastName}. In your case, it appears the account is registered under a different name. Please try again with your own bank details.`,
          type: SUBSTAGE?.KYC_BANK_NAME_MATCH_CREDIT_QUEUE,
        }),
      );
    } else if (
      productApplication?.stage === STAGE.MANDATE &&
      (productApplication?.subStage === SUBSTAGE.MANDATE_LIMIT_EXHAUSTED ||
        hasAllMandateCountExhausted)
    ) {
      dispatch(
        setModalAttributes({
          open: true,
          message: {
            label: m("label"),
            subLabel: m("subLabel"),
          },
          type: "ATTEMPT_ERROR",
        }),
      );
      return;
    } else if (
      productApplication?.stage === STAGE.EKYC &&
      productApplication?.subStage === SUBSTAGE.EKYC_MAX_ATTEMPTS_REACHED
    ) {
      dispatch(
        setModalAttributes({
          open: true,
          message: {
            label: EKYC_MESSAGES.MAX_ATTEMPT_TITLE,
            subLabel: EKYC_MESSAGES.MAX_ATTEMPT_TEXT,
          },
          type: ERROR_TYPES.ATTEMPT_ERROR,
        }),
      );
    } else if (productApplication?.stage === "CREDIT_QUEUE") {
      dispatch(
        setModalAttributes({
          open: true,
          message:
            productApplication?.message ||
            "Looks like, we would require to screen your application a little more. We will get back to you in next 24 hours.",
          type: stage,
        }),
      );
    } else if (SUB_STAGE_FAILS.includes(productApplication?.subStage)) {
      dispatch(
        setModalAttributes({
          open: true,
          message:
            productApplication?.message ||
            "Sorry! We would love to serve you in the future",
          type: "CLOSED",
        }),
      );
      return;
    } else {
      router.push(
        STAGE_BASED_ROUTING(
          stage,
          product?.loanType === PRODUCTS.PL
            ? PRODUCT_TYPES.PERSONAL_LOAN
            : null,
        ),
      );
    }
  }, [customerData]);
 
  /***************************************RENDER************************************ */
  return (
    <>
      {isLoading && <LoaderComponent open={isLoading || isFetchingGetApplication || isLoadingGetApplication} />}
      {productList?.length > 0 && (
        <Stack rowGap={2}>
          <Stack gap={1}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={1}
              flexWrap={"wrap"}
            >
              <Image src={IMAGES.leftWindow} alt="Hero" />
              <Typography variant="h6">{t("exploreproducts")}</Typography>
              <Typography variant="caption" color={WEB_COLORS.textFadeColor}>
                {t("clickproductcard")}
              </Typography>
            </Stack>
          </Stack>
          <Grid container rowGap={2}>
            {productList?.map((product) => (
              <Grid
                key={product?.id}
                item
                xs={12}
                sm={6}
                md={4}
                onClick={() => handleProuctCardClick(product)}
              >
                <ProductCard {...product} />
              </Grid>
            ))}
          </Grid>

          {tokenExist && <PreApprovedOffers />}
          {/* <CardBanner
            handleCheckCredit={handleCheckCredit}
            customerStatus={customerStatus}
          /> */}
          {/* <CardBanner handleCheckCredit={handleCheckEligibilty} customerStatus={customerStatus}/> */}

        </Stack>
      )}
    </>
  );
};

export default ProductsList;
